// src/curated/showcase.js
// The curated trips section can show only chosen DMC trips (FEATURES.CURATED_SHOWCASE_ONLY), e.g. just
// Vertical Tours' Kashmir trip, instead of the built-in curated trips. Each card comes from the DMC trip's
// built-in copy: title, cover photo, days, tags and a "from" price for 2 adults.
import { FEATURES } from "../config/features";
import { DMC_SEED_TRIPS } from "./dmc/dmcTrips";
import { useEffect, useState } from "react";
import { initialDmcState, priceDmc } from "./dmc/dmcEditor";
import { HUB_ON, getDmcTrip } from "../utils/partnerHub";
import { HORNBILL, PACKAGES, OPERATORS, PHOTOS, minPrice } from "./hornbill/hornbillData";
import { placeToQuery } from "./originPlace";

// A festival page with many operators' packages (not one DMC trip)
const hornbillCard = (c) => ({
    key: c.path, path: c.path, title: `Hornbill Festival 2026: Nagaland`, name: "Hornbill Festival",
    subtitle: `${PACKAGES.length} packages from ${Object.keys(OPERATORS).length} local operators, ${HORNBILL.datesLabel}`,
    days: 0, when: "1 to 10 Dec", vibe: ["Festival", "Culture", "Nagaland"], best_months: "December", cover: PHOTOS[HORNBILL.photo].src_small,
    gradient: "linear-gradient(135deg, #1F3D2B 0%, #14261B 55%, #A3271B 100%)", from_pp: minPrice(), partner: "", max_group: 20, festival: true,
});

export const SHOWCASE_ONLY = !!FEATURES.CURATED_SHOWCASE_ONLY && (FEATURES.CURATED_SHOWCASE || []).length > 0;

/** Places the showcased Kashmir trip covers (Jammu and Katra left out: mostly Vaishno Devi trips) */
export const SHOWCASE_PLACE_RE = /kashmir|srinagar|gulmarg|sonamarg|pahalgam|pahalgm|dal lake/i;

const fromPP = (trip) => {
    try { return Math.round(priceDmc(trip, initialDmcState(trip, { pax: 2 })).packagePerPerson / 100) * 100; } catch { return 0; }
};

const toCard = (c, t) => {
        if (!t || !c.path) return null;
        const im = (t.cover_images || [])[0];
        return {
            key: c.path, path: c.path, title: c.title || t.title, name: String(c.title || t.title || "").split(":")[0].trim(),
            subtitle: t.subtitle || "", days: (t.days || []).length, vibe: t.vibe || [], best_months: t.best_months || "",
            cover: im ? (im.src_small || im.src) : "", gradient: t.theme?.gradient || "", from_pp: fromPP(t),
            partner: t.partner?.short_name || t.partner?.name || "", max_group: t.max_group || 12,
        };
};
const seedFor = (c) => DMC_SEED_TRIPS.find((x) => x.slug === c.seed) || DMC_SEED_TRIPS.find((x) => c.path?.endsWith(`/${x.slug}`));

/** Cards from the built-in copies: { key, path, title, name, subtitle, days, vibe, best_months, cover, from_pp, partner, max_group } */
export function showcaseTrips() {
    return (FEATURES.CURATED_SHOWCASE || []).map((c) => (c.kind === "hornbill" ? hornbillCard(c) : toCard(c, seedFor(c)))).filter(Boolean);
}

/** Same cards, refreshed from the live trips on the partner hub (current rates, title, photos) */
export function useShowcaseTrips() {
    const [cards, setCards] = useState(showcaseTrips);
    useEffect(() => {
        if (!SHOWCASE_ONLY || !HUB_ON) return undefined;
        let alive = true;
        Promise.all((FEATURES.CURATED_SHOWCASE || []).map(async (c) => {
            if (c.kind === "hornbill") return hornbillCard(c);
            const [, , partnerSlug, slug] = String(c.path || "").split("/");   // /dmc/<partner>/<trip>
            const seed = seedFor(c);
            try {
                const r = await getDmcTrip(partnerSlug, slug);
                const live = r.status === 200 ? r.data?.trip : null;
                return toCard(c, live ? { ...seed, ...live, cover_images: live.cover_images?.length ? live.cover_images : seed?.cover_images, theme: live.theme || seed?.theme } : seed);
            } catch { return toCard(c, seed); }
        })).then((list) => { if (alive) setCards(list.filter(Boolean)); });
        return () => { alive = false; };
    }, []);
    return cards;
}

/** The DMC trip page link with what was picked on the home page (the trip page reads from, date, adults, days) */
// city: a name from ORIGIN_CITIES, or a place { name, lat, lng, code } from the station/airport box
export function showcaseLink(card, { city = "", date = "", adults = 2, days = 0 } = {}) {
    if (card.festival) return card.path;   // the festival page has its own search
    const q = new URLSearchParams();
    if (city) placeToQuery(q, city);
    if (date) q.set("date", date);
    if (city || date || adults !== 2) q.set("adults", String(adults));
    if (days && days !== card.days) q.set("days", String(days));
    const qs = q.toString();
    return `${card.path}${qs ? `?${qs}` : ""}`;
}
